import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

export default function AdminClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/clients"); setClients(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/clients", form);
      toast.success("Client account created");
      setOpen(false);
      setForm({ name: "", company: "", email: "", password: "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Clients</h1>
          <p className="text-slate-600 mt-1 text-sm">Onboard new clients and monitor their case volume.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-add-client-btn" className="pill-btn bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11">
              <UserPlus size={16} className="mr-2" /> Add client
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create client account</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={form.name} onChange={upd("name")} data-testid="new-client-name" className="mt-1.5" /></div>
              <div><Label>Company</Label><Input value={form.company} onChange={upd("company")} data-testid="new-client-company" className="mt-1.5" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={upd("email")} data-testid="new-client-email" className="mt-1.5" /></div>
              <div><Label>Temporary password</Label><Input value={form.password} onChange={upd("password")} data-testid="new-client-password" className="mt-1.5" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} disabled={busy} data-testid="new-client-save" className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                {busy ? "Creating…" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 border border-slate-200 rounded-2xl bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
        ) : clients.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No clients yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Checks</TableHead>
                <TableHead>Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.email}`}>
                  <TableCell className="font-medium text-slate-900">{c.name}</TableCell>
                  <TableCell>{c.company || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{c.email}</TableCell>
                  <TableCell><span className="font-mono-data text-sm">{c.checks_count}</span></TableCell>
                  <TableCell className="text-xs text-slate-500">{(c.created_at || "").slice(0, 10)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
